import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import asignIconWhite from '../assets/logos/asign-icon-white.svg';
import asignIconPrimary from '../assets/logos/asign-icon-primary.svg';

/**
 * FormTabOverlay
 * 
 * An absolutely-positioned overlay that sits on top of a specific region of
 * a form image. It renders a coloured border around the section and the
 * familiar left-side tab button that opens the video sidebar — exactly like
 * TabSection does on the rest of the site.
 *
 * Because this component is rendered INSIDE the FormViewer's scaled
 * (.form-viewer-content) div, it scales automatically with the zoom level.
 *
 * Props:
 *   sectionId    — unique id for this overlay (required, must be site-unique)
 *   top          — CSS value for distance from top of the form image, e.g. "12%"
 *   height       — CSS value for the height of the highlighted region, e.g. "22%"
 *   videoUrls    — array of video URLs, e.g. ["https://...", "https://..."]
 *   videoLabels  — array of label strings, e.g. ["ASL", "LSQ"]
 *   + all the standard video-sidebar props forwarded from FormViewer
 */
function FormTabOverlay({
  sectionId = 'form-overlay',
  top = '0%',
  height = '20%',
  videoUrls = [],
  videoLabels,
  activeSectionId,
  setActiveSectionId,
  setCurrentVideoUrl,
  setVideoSidebarOpen,
  setVideoLabels,
  setSelectedVideoIndex,
  setAllVideoUrls,
  registerSection,
  unregisterSection,
}) {
  const { i18n } = useTranslation();
  // Default to LSQ (index 1) on French pages, ASL (index 0) on English pages
  const localSelectedIndex = i18n.language === 'fr' ? 1 : 0;
  const [isHovered, setIsHovered] = useState(false);
  const overlayRef = useRef(null);

  const labels = videoLabels || videoUrls.map((_, i) => `V${i + 1}`);
  const currentVideo = videoUrls[localSelectedIndex];

  // Register so the global sidebar navigation knows about this section.
  // getEl is read at scroll time so the live DOM node is returned even
  // though we only register once.
  useEffect(() => {
    if (registerSection) {
      registerSection({ sectionId, videos: videoUrls, labels, isDefault: false, getEl: () => overlayRef.current });
    }
    return () => {
      if (unregisterSection) unregisterSection(sectionId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, registerSection, unregisterSection]);

  const isActive = activeSectionId === sectionId;

  // Keep the sidebar video in sync when this overlay is the active one
  useEffect(() => {
    if (isActive && setCurrentVideoUrl) {
      setCurrentVideoUrl(currentVideo);
    }
  }, [isActive, currentVideo, setCurrentVideoUrl]);

  const toggleVideoSidebar = () => {
    if (isActive) {
      setActiveSectionId(null);
      setVideoSidebarOpen(false);
    } else {
      setActiveSectionId(sectionId);
      setCurrentVideoUrl(currentVideo);
      setVideoSidebarOpen(true);
      if (setAllVideoUrls) setAllVideoUrls(videoUrls);
      if (setVideoLabels) setVideoLabels(labels);
      if (setSelectedVideoIndex) setSelectedVideoIndex(localSelectedIndex);
    }
  };

  return (
    <div
      ref={overlayRef}
      className={`form-tab-overlay ${isActive ? 'active' : ''}`}
      style={{ top, height }}
    >
      {/* Left tab button — same design as TabSection */}
      <div
        className={`form-tab-overlay-btn ${isActive ? 'active' : ''}`}
        onClick={toggleVideoSidebar}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title="View video"
        style={{ background: isActive || isHovered ? 'var(--color-primary)' : 'var(--color-secondary)' }}
      >
        <img
          src={isActive || isHovered ? asignIconWhite : asignIconPrimary}
          alt="ASL Icon"
          className="tab-section-icon"
        />
      </div>
    </div>
  );
}

export default FormTabOverlay;
